import products from "../data/iap-products.json";

// Admin-only category; it is always expanded before storage or game delivery.
export const IAP_REWARD_TYPE = 100;
export interface RewardInput {
  rewardType: number;
  amount: number;
  itemID?: string;
  hero?: number;
  developerBuffID?: string;
  recipe?: number;
  iapProductId?: string;
}
const kinds: Record<string, number> = {
  Item: 0, Gold: 1, Ruby: 2, Special_Premium: 3, Special_Hero: 4,
  Special_Welkin: 5, Special_Raven: 6, Special_Maid: 7, Special_School: 8,
  Special_DailyMaterial: 9, Developer_Buff: 10, FishingNet: 11,
  UnlimitedFishing: 12, RentHelper: 13, NoBoss: 14, FreeRift: 15, Recipe: 16,
};
type CatalogReward = { kind: string; amount?: number; itemId?: string | null; heroId?: number; developerBuffId?: string | null };
function wire(r: CatalogReward): RewardInput {
  const rewardType = kinds[r.kind];
  if (rewardType === undefined || !Number.isSafeInteger(r.amount ?? 1) || (r.amount ?? 1) < 1 || (rewardType === 0 && !r.itemId)) {
    throw new Error(`Unsupported catalogue reward: ${r.kind}`);
  }
  return { rewardType, amount: r.amount ?? 1,
    ...(r.itemId ? { itemID: r.itemId } : {}),
    ...(rewardType === 4 ? { hero: r.heroId } : {}),
    ...(r.developerBuffId ? { developerBuffID: r.developerBuffId } : {}) };
}
const special: Record<string, RewardInput[]> = {
  premium_mode: [{ rewardType: 3, amount: 1 }],
  raven_pass: [{ rewardType: 6, amount: 1 }],
  maid_pass: [{ rewardType: 7, amount: 1 }],
  school_pass: [{ rewardType: 8, amount: 1 }],
  daily_ruby: [{ rewardType: 5, amount: 1 }],
  daily_material_second: [{ rewardType: 9, amount: 1 }],
  rdl_no_timer_boss_5: [{ rewardType: 14, amount: 5 }],
  rdl_free_rift_entry: [{ rewardType: 15, amount: 1 }],
  rdl_rent_helper: [{ rewardType: 13, amount: 1 }],
};
export const IAP_CATALOG = products.map((p) => {
  let rewards: RewardInput[] = [];
  let pools: { draws: number; rewards: RewardInput[] }[] = [];
  let unavailable: string | null = null;
  try {
    if (special[p.id]) rewards = special[p.id];
    else if (/^(fishing_net|buff_destroyer_deal|buff_mercenary_deal)_/.test(p.id)) {
      const t = p.stateTransforms[0] as { amount: number; unit: string };
      rewards = [{ rewardType: p.id.startsWith("fishing_net") ? 11 : 10,
        amount: t.amount * (t.unit === "days" ? 24 : 1),
        ...(p.id.startsWith("buff_") ? { developerBuffID: p.id.includes("destroyer") ? "buff_destroyer" : "buff_mercenary" } : {}) }];
    } else {
      if (p.manualReviewReasons.length) throw new Error(p.manualReviewReasons.join("; "));
      rewards = p.rewards.map(wire);
      pools = p.randomRewardPools.map((pool) => ({ draws: pool.draws, rewards: pool.rewards.map(wire) }));
      if (!rewards.length && !pools.length) throw new Error("This product changes purchase state and has no deliverable rewards.");
    }
  } catch (error) { unavailable = error instanceof Error ? error.message : "Unsupported product"; }
  const summary = unavailable ?? [...rewards.map((r) =>
    `${r.itemID ?? (r.rewardType === 4 ? `Hero ${r.hero}` : Object.keys(kinds).find((k) => kinds[k] === r.rewardType))} ×${r.amount}`),
    ...pools.map((pool) => `${pool.draws} random item(s), rolled when saved`)].join(", ");
  return { id: p.id, aliases: p.aliases, rewards, pools, unavailable, summary };
});

export function expandIapRewards(input: unknown): RewardInput[] {
  if (!Array.isArray(input)) throw new Error("Rewards must be an array.");
  return input.flatMap((r: RewardInput) => {
    if (!r || typeof r !== "object") throw new Error("Invalid reward entry.");
    if (r.rewardType !== IAP_REWARD_TYPE) return [r];
    const product = IAP_CATALOG.find((p) => p.id === r.iapProductId || p.aliases.includes(r.iapProductId ?? ""));
    if (!product) throw new Error("Select a valid IAP product.");
    if (product.unavailable) throw new Error(`${product.id}: ${product.unavailable}`);
    if (!Number.isSafeInteger(r.amount) || r.amount < 1 || r.amount > 100) throw new Error("IAP quantity must be an integer from 1 to 100.");
    const result: RewardInput[] = [];
    for (let i = 0; i < r.amount; i++) {
      result.push(...product.rewards.map((reward) => ({ ...reward })));
      for (const pool of product.pools) {
        for (let draw = 0; draw < pool.draws; draw++) {
          const roll = crypto.getRandomValues(new Uint32Array(1))[0] / 4294967296;
          result.push({ ...pool.rewards[Math.floor(roll * pool.rewards.length)] });
        }
      }
    }
    return result;
  });
}
